import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Palmtree, Wallet, CheckSquare, Receipt, Mail, Phone, MapPin, Calendar, Plus, FileText, Laptop, Target, Download } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, Badge, Avatar, Loading, fmtMoney, fmtDate } from "../components/ui";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function Tile({ icon: Icon, label, value, accent, to }) {
  const inner = (
    <Card interactive={!!to} className="p-5 h-full" data-testid={`my-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${accent}`}><Icon size={19} /></div>
      <p className="mt-3 font-head text-2xl font-extrabold text-slate-900 dark:text-white">{value}</p>
      <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function MyProfile() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/me/overview").then(({ data }) => setD(data)); }, []);
  if (!d) return <Loading />;

  const p = d.profile;
  const downloadPayslip = async (s) => {
    const res = await api.get(`/me/payslips/${s.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `payslip-${s.month ? MONTHS[s.month - 1] + "-" + s.year : s.id}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  const leaveLeft = d.leaveBalances.reduce((a, b) => a + (b.remaining ?? (b.entitled - b.used)), 0);
  const latestNet = d.payslips[0]?.net;
  const openTasks = d.tasks.filter((t) => t.status !== "DONE").length;
  const pendingExp = d.expenses.filter((e) => e.status === "PENDING").length;

  if (!p) return (
    <Card className="p-10 text-center" data-testid="my-no-profile">
      <h2 className="font-head text-xl font-bold text-slate-900 dark:text-white">No employee profile linked</h2>
      <p className="mt-2 text-sm text-slate-500">Your account isn't linked to an employee record yet. Contact HR.</p>
    </Card>
  );

  return (
    <div data-testid="my-space">
      {/* Hero */}
      <Card className="mb-6 overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-accent to-blue-700" />
        <div className="flex flex-wrap items-end justify-between gap-4 px-6 pb-6">
          <div className="flex items-end gap-4">
            <div className="-mt-10 rounded-full ring-4 ring-white dark:ring-slate-900"><Avatar name={p.fullName} size={80} url={p.profilePhotoUrl} /></div>
            <div className="pb-1">
              <h1 className="font-head text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{p.fullName}</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">{p.designationName} · {p.departmentName}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2"><Badge value={p.status} /><span className="font-mono text-xs text-slate-400">{p.employeeCode}</span></div>
            </div>
          </div>
          <Link to={`/employees/${p.id}`} className="btn-outline" data-testid="view-full-profile">View full profile</Link>
        </div>
      </Card>

      {/* Quick tiles */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Tile icon={Palmtree} label="Leave Balance" value={`${leaveLeft} days`} accent="bg-emerald-500/10 text-emerald-600" to="/leave" />
        <Tile icon={Wallet} label="Latest Net Pay" value={latestNet != null ? fmtMoney(latestNet) : "—"} accent="bg-accent/10 text-accent" />
        <Tile icon={CheckSquare} label="Open Tasks" value={openTasks} accent="bg-violet-500/10 text-violet-600" to="/tasks" />
        <Tile icon={Receipt} label="Pending Expenses" value={pendingExp} accent="bg-amber-500/10 text-amber-600" to="/expenses" />
      </div>

      {/* Quick actions */}
      <div className="mb-6 flex flex-wrap gap-2">
        <Link to="/leave" className="btn-accent" data-testid="qa-leave"><Plus size={15} /> Apply Leave</Link>
        <Link to="/expenses" className="btn-outline" data-testid="qa-expense"><Plus size={15} /> Submit Expense</Link>
        <Link to="/helpdesk" className="btn-outline" data-testid="qa-ticket"><Plus size={15} /> Raise Ticket</Link>
        <Link to="/travel" className="btn-outline" data-testid="qa-travel"><Plus size={15} /> Travel Request</Link>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Leave balances */}
        <Card className="p-6">
          <h3 className="mb-4 font-head text-lg font-bold text-slate-900 dark:text-white">Leave Balances</h3>
          <div className="space-y-3">
            {d.leaveBalances.length === 0 && <p className="text-sm text-slate-400">No leave balances.</p>}
            {d.leaveBalances.map((b) => (
              <div key={b.id} data-testid={`my-balance-${b.id}`}>
                <div className="mb-1 flex items-center justify-between text-sm"><span className="text-slate-600 dark:text-slate-300">{b.leaveTypeName}</span><span className="font-mono font-semibold text-slate-800 dark:text-slate-100">{b.remaining ?? (b.entitled - b.used)}/{b.available ?? b.entitled}</span></div>
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full rounded-full bg-accent" style={{ width: `${(b.available ?? b.entitled) ? (b.used / (b.available ?? b.entitled)) * 100 : 0}%` }} /></div>
              </div>
            ))}
          </div>
        </Card>

        {/* Payslips */}
        <Card className="p-6">
          <h3 className="mb-4 flex items-center gap-2 font-head text-lg font-bold text-slate-900 dark:text-white"><FileText size={17} className="text-accent" /> My Payslips</h3>
          {d.payslips.length === 0 ? <p className="text-sm text-slate-400">No payslips yet.</p> : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="my-payslips">
              <thead><tr><th className="th">Period</th><th className="th">Gross</th><th className="th">Deductions</th><th className="th">Net</th><th className="th"></th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {d.payslips.map((s) => (
                  <tr key={s.id}><td className="td">{s.month ? `${MONTHS[s.month - 1]} ${s.year}` : "—"}</td><td className="td font-mono">{fmtMoney(s.gross)}</td><td className="td font-mono text-red-500">-{fmtMoney(s.tax + s.deductions)}</td><td className="td font-mono font-semibold text-emerald-600">{fmtMoney(s.net)}</td><td className="td"><button onClick={() => downloadPayslip(s)} data-testid={`download-payslip-${s.id}`} className="flex items-center gap-1 text-xs font-semibold text-accent hover:underline"><Download size={13} /> PDF</button></td></tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Card>

        {/* My tasks */}
        <Card className="p-6">
          <h3 className="mb-4 font-head text-lg font-bold text-slate-900 dark:text-white">My Tasks</h3>
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {d.tasks.length === 0 && <p className="text-sm text-slate-400">No tasks assigned.</p>}
            {d.tasks.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2.5" data-testid={`my-task-${t.id}`}>
                <div><p className="text-sm font-medium text-slate-800 dark:text-slate-100">{t.title}</p><p className="text-xs text-slate-400">{t.status}</p></div>
                <Badge value={t.priority} />
              </div>
            ))}
          </div>
        </Card>

        {/* My goals */}
        <Card className="p-6">
          <h3 className="mb-4 flex items-center gap-2 font-head text-lg font-bold text-slate-900 dark:text-white"><Target size={17} className="text-accent" /> My Goals</h3>
          <div className="space-y-4">
            {d.goals.length === 0 && <p className="text-sm text-slate-400">No goals set.</p>}
            {d.goals.map((g) => (
              <div key={g.id} data-testid={`my-goal-${g.id}`}>
                <div className="mb-1 flex items-center justify-between text-sm"><span className="font-medium text-slate-700 dark:text-slate-200">{g.title}</span><span className="font-mono text-xs text-slate-500">{g.progress}%</span></div>
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full rounded-full bg-accent" style={{ width: `${g.progress}%` }} /></div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Contact + assets row */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <h3 className="mb-4 font-head text-lg font-bold text-slate-900 dark:text-white">My Details</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[[Mail, "Email", p.email], [Phone, "Phone", p.phone || "—"], [MapPin, "Location", p.locationName || "—"], [Calendar, "Joined", fmtDate(p.joinDate)]].map(([Ic, l, v]) => (
              <div key={l} className="flex items-center gap-3 rounded-md border border-slate-100 px-3 py-2.5 dark:border-slate-800"><Ic size={16} className="text-slate-400" /><div><p className="lbl">{l}</p><p className="text-sm font-medium text-slate-800 dark:text-slate-100">{v}</p></div></div>
            ))}
          </div>
        </Card>
        <Card className="p-6">
          <h3 className="mb-4 flex items-center gap-2 font-head text-lg font-bold text-slate-900 dark:text-white"><Laptop size={17} className="text-accent" /> My Assets</h3>
          <div className="space-y-2">
            {d.assets.length === 0 && <p className="text-sm text-slate-400">No assets assigned.</p>}
            {d.assets.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 dark:bg-slate-800/50" data-testid={`my-asset-${a.id}`}>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{a.name}</span><span className="font-mono text-xs text-slate-400">{a.serialNumber}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {d.onboarding && (
        <Card className="mt-6 p-6">
          <div className="mb-4 flex items-center justify-between"><h3 className="font-head text-lg font-bold text-slate-900 dark:text-white">My Onboarding</h3><span className="font-head text-xl font-extrabold text-accent">{d.onboarding.progress}%</span></div>
          <Link to="/onboarding" className="text-sm font-semibold text-accent hover:underline">View checklist →</Link>
        </Card>
      )}
    </div>
  );
}
